import { JSX, lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import "./App.css";

import { MainWrapper } from "./MainWrapper";
import { globalData } from "./globals";
import { LookstyLoader } from "./GloablComponents/Loader/Loader";

import { AuthLayout } from "./Pages/Auth/AuthLayout";
import { FeedPage } from "./Pages/Feed/Feed";
import { FeedDetailsPage } from "./Pages/FeedDetails/FeedDetails";
import { PublicMasterProfilePage } from "./Pages/PublicMasterProfile/PublicMasterProfilePage";
import { CreateBookingPage } from "./Pages/CreateBooking/CreateBookingPage";
import { CreateBookingRequestPage } from "./Pages/CreateBookingRequestPage/CreateBookingRequestPage";
import { MasterPostPage } from "./Pages/MasterPost/MasterPostPage";
import { MasterLandingPage } from "./Pages/MasterLanding/MasterLandingPage";
import { MasterLandingSuccessPage } from "./Pages/MasterLanding/MasterLandingSuccessPage";
import { AdminMasterLeadsPage } from "./Pages/Admin/AdminMasterLeads/AdminMasterLeadsPage";

const Register = lazy(() => import("./Pages/Register/Register").then((m) => ({ default: m.Register })));
const Login = lazy(() => import("./Pages/Login/Login").then((m) => ({ default: m.Login })));
const NotFoundPage = lazy(() => import("./Pages/Errors/404").then((m) => ({ default: m.NotFoundPage })));

const SearchPage = lazy(() => import("./Pages/Search/Search").then((m) => ({ default: m.SearchPage })));
const CollectionDetailsPage = lazy(() => import("./Pages/CollectionDetails/CollectionDetailsPage").then((m) => ({ default: m.CollectionDetailsPage })));

const UserProfilePage = lazy(() => import("./Pages/UserProfile/UserProfile").then((m) => ({ default: m.UserProfilePage })));
const ProfileSettingsPage = lazy(() => import("./Pages/UserProfile/ProfileSettings/ProfileSettingsPage").then((m) => ({ default: m.ProfileSettingsPage })));
const CreateMasterProfilePage = lazy(() => import("./Pages/UserProfile/CreateMasterProfile/CreateMasterProfilePage").then((m) => ({ default: m.CreateMasterProfilePage })));
const EditProfilePage = lazy(() => import("./Pages/UserProfile/EditProfile/EditProfilePage").then((m) => ({ default: m.EditProfilePage })));

const MasterProfilePage = lazy(() => import("./Pages/MasterProfile/MasterProfile").then((m) => ({ default: m.MasterProfilePage })));
const EditMasterPostPage = lazy(() => import("./Pages/MasterPost/EditMasterPostPage").then((m) => ({ default: m.EditMasterPostPage })));
const WorkingHoursPage = lazy(() => import("./Pages/MasterWorkingHours/WorkingHoursPage").then((m) => ({ default: m.WorkingHoursPage })));
const MasterBookingTasksPage = lazy(() => import("./Pages/MasterBookingTasks/MasterBookingTasksPage").then((m) => ({ default: m.MasterBookingTasksPage })));

const UserBookingsPage = lazy(() => import("./Pages/UserBookingsPage/UserBookingsPage").then((m) => ({ default: m.UserBookingsPage })));
const UserMastersPage = lazy(() => import("./Pages/UserMasters/UserMastersPage").then((m) => ({ default: m.UserMastersPage })));
const FavoritePostsPage = lazy(() => import("./Pages/FavoritePosts/FavoritePostsPage").then((m) => ({ default: m.FavoritePostsPage })));

const AdminLayout = lazy(() => import("./Pages/Admin/AdminLayout/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const AdminPostsPage = lazy(() => import("./Pages/Admin/AdminPostsPage/AdminPostsPage").then((m) => ({ default: m.AdminPostsPage })));
const AdminMastersPage = lazy(() => import("./Pages/Admin/AdminMastersPage/AdminMastersPage").then((m) => ({ default: m.AdminMastersPage })));
const AdminMasterDetailsPage = lazy(() => import("./Pages/Admin/AdminMasterDetails/AdminMasterDetailsPage").then((m) => ({ default: m.AdminMasterDetailsPage })));
const AdminAnalyticsPage = lazy(() => import("./Pages/Admin/AdminAnalyticsPage/AdminDashboardPage").then((m) => ({ default: m.AdminAnalyticsPage })));
const AdminBookingRequestsPage = lazy(() => import("./Pages/Admin/BookingRequests/AdminBookingRequestsPage").then((m) => ({ default: m.AdminBookingRequestsPage })));
const AdminCollectionDetailsPage = lazy(() => import("./Pages/Admin/Collections/AdminCollectionDetailsPage").then((m) => ({ default: m.AdminCollectionDetailsPage })));
const AdminCollectionsPage = lazy(() => import("./Pages/Admin/Collections/AdminCollectionsPage").then((m) => ({ default: m.AdminCollectionsPage })));

const AppLoader = () => {
  return (
    <main className="app-loader-page">
      <LookstyLoader title="Загружаем Looksty" subtitle="Готовим страницу" />
    </main>
  );
};

const App = () => {
  return (
    <Suspense fallback={<AppLoader />}>
      <Routes>
        <Route path="/" element={<Navigate to="/feed" replace />} />
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
        </Route>
        <Route path="/master/:id/booking-request" element={<CreateBookingRequestPage />} />
        <Route path="/master/:id" element={<PublicMasterProfilePage />} />
        <Route path="/master/posts/:id" element={<MasterPostPage />} />
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/feed/:id" element={<FeedDetailsPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/collections/:slug" element={<CollectionDetailsPage />} />
        <Route path="/master-lending" element={<MasterLandingPage />} />
        <Route path="/master-lending/success" element={<MasterLandingSuccessPage />} />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<AdminAnalyticsPage />} />
          <Route path="posts" element={<AdminPostsPage />} />
          <Route path="masters" element={<AdminMastersPage />} />
          <Route path="masters/:id" element={<AdminMasterDetailsPage />} />
          <Route path="booking-requests" element={<AdminBookingRequestsPage />} />
          <Route path="collections" element={<AdminCollectionsPage />} />
          <Route path="collections/:id" element={<AdminCollectionDetailsPage />} />
          <Route path="master-leads" element={<AdminMasterLeadsPage />} />
        </Route>
        <Route
          element={
            <RequireAuth>
              <MainWrapper />
            </RequireAuth>
          }
        >
          <Route path="/profile" element={<UserProfilePage />} />
          <Route path="/profile/bookings" element={<UserBookingsPage />} />
          <Route path="/profile/masters" element={<UserMastersPage />} />
          <Route path="/profile/collections" element={<FavoritePostsPage />} />

          <Route path="/settings" element={<ProfileSettingsPage />} />
          <Route path="/settings/profile" element={<EditProfilePage />} />

          <Route path="/master/me" element={<MasterProfilePage />} />
          <Route path="/master/create" element={<CreateMasterProfilePage />} />
          <Route path="/master/:id/booking" element={<CreateBookingPage />} />
          <Route path="/master/posts/:id/edit" element={<EditMasterPostPage />} />
          <Route path="/master/working-hours" element={<WorkingHoursPage />} />
          <Route path="/master/bookings/tasks" element={<MasterBookingTasksPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const authToken = localStorage.getItem("zwpat0_1");

  if (!authToken || authToken.length === 0) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const RequireAdmin = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const authToken = localStorage.getItem("zwpat0_1");

  if (!authToken || authToken.length === 0) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (globalData.CurrentUser?.role !== "admin") {
    return <Navigate to="/feed" replace />;
  }

  return children;
};

export default App;
